import { loadFontFamily, usePublisherStyling } from '@ad-rendering/publisher-style';
import { isEnhanceable } from '@web-rendering/groundcontrol-butler-helpers';
import { useErrorHandler, usePayload } from '@web-rendering/groundcontrol-core';
import { CreativeType } from '@web-rendering/groundcontrol-shared-types';
import { reportError } from '@web-rendering/groundcontrol-monitoring';
import { recreateChildScriptTags } from '@web-rendering/groundcontrol-utils';
import { Fragment } from 'preact';
import { forwardRef } from 'preact/compat';
import { useEffect, useRef } from 'preact/hooks';
import { EnhancedTemplate } from './enhanced/enhanced.template';
import type { TemplateProps } from './types';
import { UnenhancedTemplate } from './unenhanced/unenhanced.component';

export const Template = forwardRef<HTMLDivElement, TemplateProps>((props: TemplateProps, ref) => {
  const [error] = useErrorHandler();
  const adWrapperRef = useRef<HTMLElement | null>(null);
  const { styles, error: publisherStyleError } = usePublisherStyling();
  const payload = usePayload();

  useEffect(() => {
    if (adWrapperRef.current === null) return;
    // @ts-expect-error: Property 'base' does not exist on type 'HTMLElement' (is it correct on a Fragment?)
    const baseElement = adWrapperRef.current.base;
    if (baseElement) recreateChildScriptTags(baseElement.parentElement);
  }, [adWrapperRef]);

  useEffect(() => {
    if (publisherStyleError) {
      reportError(publisherStyleError, {
        adserverRequestId: payload?.id,
        partnerId: payload?.creative.partnerId,
        networkId: payload?.network.networkId,
        callerId: payload?.network.callerId
      });
      return;
    }

    if (!styles || !styles.fontFamily) return;
    loadFontFamily(styles.fontFamily);
  }, [styles, publisherStyleError]);

  const { adserverRequestId, creative, network, country, className, children } = props;
  const commonProps = { adserverRequestId, creative, network, className, country, children };

  const creativeType = payload?.creative.type;

  const shouldRenderEnhancedTemplate =
    payload &&
    (isEnhanceable(payload.creative, payload?.enhancement) || creativeType === CreativeType.Native) &&
    !error;

  return (
    <div
      data-testid="str-compiled-template"
      className="str-compiled-template"
      style={{ width: '100%', height: '100%' }}
      ref={ref}
    >
      {shouldRenderEnhancedTemplate ? (
        <Fragment ref={adWrapperRef}>
          <EnhancedTemplate {...commonProps} onEnhancementClick={props.onEnhancementClick} />
        </Fragment>
      ) : (
        <UnenhancedTemplate {...commonProps} />
      )}
    </div>
  );
});
